import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import type { FachItem } from '../../../lib/courses';
import { getSubjectLabels } from '@/lib/settings';
import { cn } from '../../../lib/utils';
import { formatDate } from '../../../lib/dateFormat';
import type { DatePageModel, SelectedSlot } from './types';

type StepDateSelectionProps = Readonly<{
  selectedSlot: NonNullable<SelectedSlot> | null;
  step2HintText: string;
  datePageModel: DatePageModel;
  date: string;
  startTime: string;
  teacherDayLoading: boolean;
  teacherDayError: string;
  hasTeacherCollision: boolean;
  showSelectedSummary?: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  onSelectDate: (nextDate: string, nextStartTime: string) => void;
  addMinutes: (hhmm: string, minutes: number) => string;
  /** Einleitung über der Datumsauswahl (Standard: Probestunde). */
  intro?: string;
  /** Frühestes wählbare Datum (YYYY-MM-DD); frühere Termine sind gesperrt. */
  minDate?: string;
  /** Dauer für die angezeigte Endzeit (Standard 30 Minuten). */
  durationMin?: number;
  /**
   * Fachwahl über der Datumsauswahl — für den Start aus dem Terminkalender,
   * der Schritt 1 überspringt (T-0308).
   */
  fachAuswahl?: {
    fachOptions: FachItem[];
    fachId?: number;
    onSelectFach: (fachId: number) => void;
  };
}>;

const StepDateSelection = ({
  selectedSlot,
  step2HintText,
  datePageModel,
  date,
  startTime,
  teacherDayLoading,
  teacherDayError,
  hasTeacherCollision,
  showSelectedSummary = true,
  onPrevPage,
  onNextPage,
  onSelectDate,
  addMinutes,
  intro = 'Wählen Sie ein Datum für die Probestunde aus.',
  minDate,
  durationMin = 30,
  fachAuswahl,
}: StepDateSelectionProps) => {
  const showStatusBox = teacherDayLoading || Boolean(teacherDayError) || hasTeacherCollision;

  if (!selectedSlot) {
    return <div className="py-2 text-sm text-muted-foreground">Bitte zuerst einen Slot in Schritt 1 wählen.</div>;
  }

  return (
    <div className="py-2">
      {fachAuswahl ? (
        <div className="mb-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {getSubjectLabels().singular}
          </div>
          <Select
            value={fachAuswahl.fachId ? String(fachAuswahl.fachId) : ''}
            onValueChange={(v) => fachAuswahl.onSelectFach(Number(v))}
          >
            <SelectTrigger aria-label={`${getSubjectLabels().singular} auswählen`}>
              <SelectValue placeholder={`${getSubjectLabels().singular} auswählen`} />
            </SelectTrigger>
            <SelectContent>
              {fachAuswahl.fachOptions.map((f) => (
                <SelectItem key={f.id} value={String(f.id)}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Termin</div>
      <div className="mb-3 text-xs text-muted-foreground">
        {intro} {step2HintText}
      </div>
      <div className="max-h-[52vh] overflow-y-auto pr-1">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(datePageModel?.pageDates || []).map((d) => {
            const locked = Boolean(minDate && d.d < minDate);
            return (
            <Button
              key={d.d}
              type="button"
              variant="outline"
              disabled={locked}
              className={cn(
                'interactive-lift h-auto justify-start rounded-md p-3 text-left transition-colors',
                date === d.d ? 'border-primary bg-primary/5' : 'border-muted hover:bg-muted/50',
                d.isPast ? 'border-muted bg-muted/50 text-muted-foreground' : '',
                d.isBeforeAnchor && !d.isPast ? 'opacity-75' : '',
                locked ? 'cursor-not-allowed opacity-50' : ''
              )}
              onClick={() => onSelectDate(d.d, selectedSlot.startTime.slice(0, 5))}
            >
              <div className="flex min-w-0 flex-col items-start">
                <div className="text-sm font-medium">{formatDate(d.d)}</div>
                <div className="mt-1 whitespace-nowrap text-xs text-muted-foreground">
                  {selectedSlot.startTime.slice(0, 5)} - {addMinutes(selectedSlot.startTime.slice(0, 5), durationMin)}
                </div>
              </div>
            </Button>
            );
          })}
        </div>
      </div>
      {datePageModel ? (
        <div className="mt-3 grid grid-cols-3 items-center gap-2">
          <div className="justify-self-start">
            <Button type="button" variant="outline" size="sm" onClick={onPrevPage} disabled={!datePageModel.canPrev}>
              Zurück
            </Button>
          </div>
          <span className="min-w-[92px] text-center text-xs text-muted-foreground">{datePageModel.pageLabel}</span>
          <div className="justify-self-end">
            <Button type="button" variant="outline" size="sm" onClick={onNextPage} disabled={!datePageModel.canNext}>
              Weiter
            </Button>
          </div>
        </div>
      ) : null}
      {showSelectedSummary && date && startTime && showStatusBox ? (
        <div className="mt-3 rounded-md border p-3 text-xs">
          {teacherDayLoading ? <div className="text-muted-foreground">Lehrerbelegungen werden geladen…</div> : null}
          {teacherDayError ? <div className="font-medium text-destructive">{teacherDayError}</div> : null}
          {hasTeacherCollision ? <div className="font-medium text-red-700">Warnung: Kollision mit bestehendem Termin.</div> : null}
        </div>
      ) : null}
    </div>
  );
};

export default StepDateSelection;
